import Header from "components/Header";
import SideBar from "components/SideBar";
import { RouteName } from "constants/route";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ISosResponse } from "models/sos";
import { useDataLoadingContext } from "contexts/DataLoadingContext";
import { useSocketIoContext } from "contexts/SocketIoContext";
import { playSosRingingSound } from "helpers/audioNotification";

export default function MainLayout() {
  const [sideBarCollapse, setSideBarCollapse] = useState<boolean>(false);
  const [expand, setExpand] = useState<boolean>(false);
  const { loadSos } = useDataLoadingContext();
  const { socket } = useSocketIoContext();
  const location = useLocation();
  const isValidToken = useMemo(() => {
    try {
      jwtDecode(localStorage.getItem("token") ?? "");
      return true;
    } catch (error) {
      return false;
    }
  }, [location.pathname]); // eslint-disable-line

  // Initialize audio context on first user interaction
  // Browsers require user interaction before allowing audio playback
  useEffect(() => {
    const initAudio = async () => {
      try {
        const { initializeAudio } = await import("helpers/audioNotification");
        await initializeAudio();
        console.log("🔊 Audio context initialized on user interaction");
      } catch (error) {
        console.log("Audio context will initialize when SOS event occurs");
      }
    };

    // Initialize audio on any user interaction (click, keypress, etc.)
    const events = ["click", "keydown", "touchstart", "mousedown"];
    const initOnce = () => {
      initAudio();
      // Remove all listeners after first interaction
      events.forEach(event => document.removeEventListener(event, initOnce));
    };

    events.forEach(event => {
      document.addEventListener(event, initOnce, { once: true, passive: true });
    });

    return () => {
      events.forEach(event => document.removeEventListener(event, initOnce));
    };
  }, []);

  const toggleSideBarCollapse = () => setSideBarCollapse((prev) => !prev);
  const toggleExpand = () => setExpand((prev) => !prev);

  const onSos = useCallback(async (data: any) => {
    console.log("🚨 SOS event received:", data);
    console.log("🚨 Socket connection status:", socket?.connected);
    console.log("🚨 Socket ID:", socket?.id);

    // Play ringing sound notification when new SOS is received
    try {
      await playSosRingingSound();
    } catch (error) {
      console.error("Failed to play SOS sound:", error);
    }

    // Reload SOS list to show the new SOS
    loadSos();
  }, [loadSos, socket]); // eslint-disable-line

  useEffect(() => {
    if (socket) {
      console.log("🔌 Setting up SOS socket listener...");
      console.log("🔌 Socket connected:", socket.connected);
      console.log("🔌 Socket ID:", socket.id);

      // Debug handler for all events
      let debugHandler: ((...args: any[]) => void) | null = null;
      if (typeof (socket as any).onAny === "function") {
        debugHandler = (...args: any[]) => {
          console.log("📨 Socket event received (any):", args);
        };
        (socket as any).onAny(debugHandler);
      }

      // Listen for SOS event - backend might send ISosWebsocketResponse or ISosResponse
      socket.on("sos", onSos);
      socket.on("activate-sos", onSos); // Match preferred backend event

      // Try alternative event names that might be used
      socket.on("sos_alert", onSos);
      socket.on("new_sos", onSos);
      socket.on("sos_created", onSos);

      // Also listen for connection status
      socket.on("connect", () => {
        console.log("✅ Socket connected for SOS notifications");
      });

      socket.on("disconnect", () => {
        console.log("❌ Socket disconnected - SOS notifications may not work");
      });

      return () => {
        console.log("🔌 Cleaning up SOS socket listener");
        socket.off("sos", onSos);
        socket.off("activate-sos", onSos);
        socket.off("sos_alert", onSos);
        socket.off("new_sos", onSos);
        socket.off("sos_created", onSos);
        if (debugHandler && typeof (socket as any).offAny === "function") {
          (socket as any).offAny(debugHandler);
        }
      };
    } else {
      console.warn("⚠️ Socket is not available - SOS notifications will not work");
    }
  }, [socket, onSos]); // eslint-disable-line

  if (localStorage.getItem("token") === null || !isValidToken) {
    try {
      socket?.disconnect();
    } catch (error) { }
    return <Navigate to={RouteName.LOGIN} />;
  }

  return (
    <div className="w-full">
      <SideBar
        sideBarCollapse={sideBarCollapse}
        toggleSideBarCollapse={toggleSideBarCollapse}
      />
      <div
        className={`w-full ${sideBarCollapse ? "pl-20" : "pl-260"
          } transition-all duration-300 min-h-screen flex flex-col`}
      >
        <div
          className={`w-full relative ${expand ? "px-0" : "px-9"
            } pt-4 h-full flex flex-col grow`}
        >
          <Header expand={expand} toggleExpand={toggleExpand} />
          <Outlet />
        </div>
      </div>
    </div>
  );
}
